/* eslint-disable */
import OpenAI from 'openai'
import { loadConfig } from '@/configLoader'
import * as BaseController from '@/controllers/BaseController'
import { useOAuthService } from '@/services/OAuthService'
import moment from 'moment-timezone'
import { loadThreadId, saveThreadId, clearThreadId } from './chatStorage'
import axios from 'axios'

// Constants
const CONSTANTS = {
  POLLING_INTERVAL: 1500,
  TOOL_CALL_TIMEOUT: 3000,
  MAX_AVAILABLE_ROOMS: 15,
  MAX_POI_LIMIT: 100,
  MAX_DEPARTURES: 10,
  MAX_MESSAGES_PER_THREAD: 8,
  CACHE_DURATION: 1 * 60 * 1000,
  EXCLUDED_CATEGORIES: ['wc', 'toilets', 'restrooms', 'elevator', 'kitchen', 'printer', 'lager', 'food and beverages'],
  SMALL_TALK_PATTERNS: ['hi', 'hello', 'hey', 'how are you', 'good morning', 'good evening'],
  DEFAULT_TIMEZONE: 'Europe/Berlin',
}

// Logger utility
class Logger {
  constructor(enabled = true) {
    this.enabled = enabled
  }

  log(message, data = null) {
    if (this.enabled) {
      console.log(`🔧 ${new Date().toISOString()} - ${message}`, data || '')
    }
  }

  error(message, error = null) {
    console.error(`❌ ${new Date().toISOString()} - ${message}`, error || '')
  }

  warn(message, data = null) {
    if (this.enabled) {
      console.warn(`⚠️ ${new Date().toISOString()} - ${message}`, data || '')
    }
  }

  // NEW: Token usage logging
  tokenUsage(usage) {
    if (this.enabled && usage) {
      console.log(`💰 Token Usage - Input: ${usage.inputTokens}, Output: ${usage.outputTokens}, Total: ${usage.totalTokens}`)
    }
  }
}

// Cache manager
class CacheManager {
  constructor(ttl = CONSTANTS.CACHE_DURATION) {
    this.cache = new Map()
    this.ttl = ttl
  }

  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
    })
  }

  get(key) {
    const item = this.cache.get(key)
    if (!item) return null

    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key)
      return null
    }

    return item.value
  }

  clear() {
    this.cache.clear()
  }

  // NEW: Get cache stats
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    }
  }
}

// OpenAI Assistant Manager
class OpenAIAssistantManager {
  constructor() {
    this.openai = null
    this.assistantId = null
    this.threadId = null
    this.messageCount = 0 // NEW: Track messages for rotation
    this.cache = new CacheManager()
    this.logger = new Logger(process.env.NODE_ENV !== 'production')
    this.oauthService = useOAuthService()
  }

  async initialize() {
    if (this.openai) return

    try {
      const config = await loadConfig()
      this.logger.log('Config loaded for OpenAI initialization')

      // Note: In production, this should be handled server-side
      this.openai = new OpenAI({
        apiKey: config.openAiAPI,
        dangerouslyAllowBrowser: true,
      })

      this.assistantId = config.openAiAssistantId
      this.logger.log(`Assistant ID set: ${this.assistantId}`)
    } catch (error) {
      this.logger.error('Failed to initialize OpenAI', error)
      throw new Error('OpenAI initialization failed')
    }
  }

  async ensureThread() {
    // NEW: Check if we need to rotate thread
    if (this.messageCount >= CONSTANTS.MAX_MESSAGES_PER_THREAD) {
      this.logger.log(`🔄 Rotating thread after ${this.messageCount} messages to save tokens`)
      await this.rotateThread()
    }

    if (this.threadId) return this.threadId

    this.threadId = await loadThreadId()

    if (!this.threadId) {
      const thread = await this.openai.beta.threads.create()
      this.threadId = thread.id
      this.messageCount = 0 // RESET: Reset counter for new thread
      await saveThreadId(this.threadId)
      this.logger.log(`New thread created: ${this.threadId}`)
    }

    return this.threadId
  }

  // NEW: Thread rotation method
  async rotateThread() {
    try {
      if (this.threadId) {
        await clearThreadId()
        this.logger.log(`Cleared old thread: ${this.threadId}`)
      }
      this.threadId = null
      this.messageCount = 0
      this.cache.clear() // Clear cache on rotation
    } catch (error) {
      this.logger.error('Failed to rotate thread', error)
      // Continue anyway - will create new thread
      this.threadId = null
      this.messageCount = 0
    }
  }

  isSmallTalk(message) {
    const normalizedMessage = message.trim().toLowerCase()
    return CONSTANTS.SMALL_TALK_PATTERNS.includes(normalizedMessage)
  }

  async sendMessage(message) {
    try {
      if (this.isSmallTalk(message)) {
        return this.createResponse('Hello! How can I assist you today with the PIA smart building app?', {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        })
      }

      await this.initialize()
      const threadId = await this.ensureThread()

      await this.openai.beta.threads.messages.create(threadId, {
        role: 'user',
        content: message,
      })

      this.messageCount++ // NEW: Increment message counter
      this.logger.log(`Message count: ${this.messageCount}/${CONSTANTS.MAX_MESSAGES_PER_THREAD}`)

      const run = await this.openai.beta.threads.runs.create(threadId, {
        assistant_id: this.assistantId,
      })

      const completedRun = await this.pollRunCompletion(run)
      const reply = await this.getAssistantReply(threadId)

      const response = this.createResponse(reply, completedRun.usage)

      // NEW: Log token usage
      this.logger.tokenUsage(response.tokenUsage)

      return response
    } catch (error) {
      this.logger.error('Assistant error', error)
      return this.createErrorResponse(error)
    }
  }

  async pollRunCompletion(run) {
    let currentRun = run

    while (!['completed', 'failed', 'cancelled'].includes(currentRun.status)) {
      await this.sleep(CONSTANTS.POLLING_INTERVAL)

      currentRun = await this.openai.beta.threads.runs.retrieve(run.id, {
        thread_id: this.threadId,
      })

      if (currentRun.status === 'requires_action') {
        await this.handleToolCalls(currentRun)
      }
    }

    if (currentRun.status !== 'completed') {
      throw new Error(`Run ${currentRun.status}: ${currentRun.last_error?.message || 'Unknown error'}`)
    }

    return currentRun
  }

  async handleToolCalls(run) {
    const toolCalls = run.required_action?.submit_tool_outputs?.tool_calls || []
    const toolHandler = new ToolHandler(this.cache, this.logger, this.oauthService)

    const toolOutputs = await Promise.all(
      toolCalls.map(async toolCall => {
        try {
          const args = JSON.parse(toolCall.function.arguments)
          const output = await toolHandler.handle(toolCall.function.name, args)

          return {
            tool_call_id: toolCall.id,
            output: JSON.stringify(output),
          }
        } catch (error) {
          this.logger.error(`Tool call failed: ${toolCall.function.name}`, error)
          return {
            tool_call_id: toolCall.id,
            output: JSON.stringify({ error: error.message }),
          }
        }
      })
    )

    await this.openai.beta.threads.runs.submitToolOutputs(run.id, {
      thread_id: this.threadId,
      tool_outputs: toolOutputs,
    })
  }

  async getAssistantReply(threadId) {
    const messages = await this.openai.beta.threads.messages.list(threadId)
    const replyMsg = messages.data.find(m => m.role === 'assistant')
    return replyMsg?.content?.[0]?.text?.value || 'No reply.'
  }

  createResponse(reply, usage = {}) {
    const tokenUsage = {
      inputTokens: usage.prompt_tokens ?? 0,
      outputTokens: usage.completion_tokens ?? 0,
      totalTokens: usage.total_tokens ?? (usage.prompt_tokens ?? 0) + (usage.completion_tokens ?? 0),
    }

    return {
      reply,
      threadId: this.threadId,
      tokenUsage,
      messageCount: this.messageCount, // NEW: Include message count in response
      cacheStats: this.cache.getStats(), // NEW: Include cache stats
    }
  }

  createErrorResponse(error) {
    return {
      reply: `❌ Assistant failed: ${error.message}`,
      threadId: this.threadId,
      tokenUsage: { inputTokens: 0, outputTokens: 0, totalTokens: 0 },
      messageCount: this.messageCount,
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // NEW: Get current stats
  getStats() {
    return {
      threadId: this.threadId,
      messageCount: this.messageCount,
      maxMessages: CONSTANTS.MAX_MESSAGES_PER_THREAD,
      cacheStats: this.cache.getStats(),
    }
  }
}

// Tool Handler
class ToolHandler {
  constructor(cache, logger, oauthService) {
    this.cache = cache
    this.logger = logger
    this.oauthService = oauthService
  }

  async handle(name, args) {
    this.logger.log(`Handling tool: ${name}`, args)

    const handlers = {
      get_floors_list: () => this.getFloorsList(),
      get_all_categories: () => this.getAllCategories(),
      get_poi_details: () => this.getPoiDetails(args),
      get_floors_with_Categories: () => this.getFloorsWithCategories(),
      get_real_time_pois: () => this.getRealTimePois(args),
      get_categories_by_floor: () => this.getCategoriesByFloor(args),
      get_pois_by_floor_and_category: () => this.getPoisByFloorAndCategory(args),
      get_pois_by_category_id: () => this.getPoisByCategoryId(args),
      get_available_rooms: () => this.getAvailableRooms(args),
      do_reservation: () => this.doReservation(args),
      get_user_reservations_by_time: () => this.getUserReservationsByTime(args),
      get_current_datetime_berlin: () => this.getCurrentDatetimeBerlin(),
      get_departures: () => this.getDepartures(args),
      get_departures_by_coordinates: () => this.getDeparturesByCoordinates(),
    }

    const handler = handlers[name]
    if (!handler) {
      return { error: `Unknown tool: ${name}` }
    }

    try {
      return await handler()
    } catch (error) {
      this.logger.error(`Tool handler error: ${name}`, error)
      return { error: error.message }
    }
  }

  async getCachedData(key, fetcher) {
    let data = this.cache.get(key)
    if (!data) {
      this.logger.log(`Cache miss for: ${key}`)
      data = await fetcher()
      this.cache.set(key, data)
    } else {
      this.logger.log(`Cache hit for: ${key}`)
    }
    return data
  }

  async getFloors() {
    return this.getCachedData('floors', BaseController.getFloors)
  }

  async getCategories() {
    return this.getCachedData('categories', BaseController.getPoiCategories)
  }

  async getPois() {
    return this.getCachedData('pois', BaseController.getPois)
  }

  async getFloorsList() {
    const floors = await this.getFloors()
    return floors.map(f => f.name)
  }

  async getAllCategories() {
    const categories = await this.getCategories()
    return categories.map(c => c.name)
  }

  async getPoiDetails(args) {
    const pois = await this.getPois()
    const poi = pois.find(p => p.name === args.poiName)

    if (!poi) {
      return { error: 'POI not found' }
    }

    // SIMPLIFIED: Return minimal data to save tokens
    return {
      id: poi.id,
      name: poi.name,
      capacity: poi.capacity,
    }
  }

  async getFloorsWithCategories() {
    const [floors, categories, pois] = await Promise.all([this.getFloors(), this.getCategories(), this.getPois()])

    return floors
      .map(floor => {
        const floorPois = this.filterPoisByFloor(pois, floor.id, categories)
        const categoryNames = this.extractCategoryNames(floorPois, categories)

        return {
          floor: floor.name,
          categories: Array.from(categoryNames),
        }
      })
      .filter(entry => entry.categories.length > 0)
  }

  filterPoisByFloor(pois, floorId, categories) {
    return pois.filter(poi => {
      const hasFloor = Array.isArray(poi.floor?.ids) && poi.floor.ids.includes(floorId)
      const hasCategories = Array.isArray(poi.poiCategories?.ids) && poi.poiCategories.ids.length > 0
      const hasValidCategory = poi.poiCategories?.ids.some(catId => {
        const cat = categories.find(c => c.id === catId)
        return cat && !CONSTANTS.EXCLUDED_CATEGORIES.includes(cat.name.toLowerCase())
      })

      return hasFloor && hasCategories && hasValidCategory
    })
  }

  extractCategoryNames(pois, categories) {
    const names = new Set()

    pois.forEach(poi => {
      poi.poiCategories.ids.forEach(catId => {
        const cat = categories.find(c => c.id === catId)
        if (cat && !CONSTANTS.EXCLUDED_CATEGORIES.includes(cat.name.toLowerCase())) {
          names.add(cat.name)
        }
      })
    })

    return names
  }

  async getRealTimePois(args) {
    const from = new Date(args.from)
    const until = new Date(args.until)

    // ADDED: Simple caching for real-time POIs
    const cacheKey = `realtime_pois_${args.from}_${args.until}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      this.logger.log('Using cached real-time POIs')
      return cached
    }

    const availableIds = await BaseController.getAvailablePois(from, until)

    if (!Array.isArray(availableIds)) {
      throw new Error('Failed to fetch available POIs')
    }

    const [pois, floors, categories] = await Promise.all([this.getPois(), this.getFloors(), this.getCategories()])

    const enrichedPois = availableIds
      .slice(0, CONSTANTS.MAX_POI_LIMIT) // KEPT: Original limit to show all meeting rooms
      .map(id => pois.find(p => p.id === id))
      .filter(Boolean)
      .map(poi => this.enrichPoi(poi, floors, categories))

    const grouped = this.groupPoisByFloorAndCategory(enrichedPois)

    // Cache the result
    this.cache.set(cacheKey, grouped)

    // Cache the result temporarily for booking resolution (smaller cache)
    this.cache.set('tempRealTimePois', {
      from: args.from,
      until: args.until,
      timestamp: Date.now(),
      grouped: grouped, // REMOVED: flatList to save memory
    })

    return grouped
  }

  enrichPoi(poi, floors, categories) {
    const floorId = poi?.floors?.ids?.[0] || poi?.floorId
    const categoryId = poi.categoryId || poi.poiCategories?.ids?.[0]

    // SIMPLIFIED: Return minimal data to save tokens
    return {
      poiId: poi.id,
      capacity: poi.capacity || 0,
      floor: floors.find(f => f.id === floorId)?.name || 'Unknown',
      category: categories.find(c => c.id === categoryId)?.name || 'Unknown',
    }
  }

  groupPoisByFloorAndCategory(enrichedPois) {
    const grouped = {}

    enrichedPois.forEach(poi => {
      if (!grouped[poi.floor]) grouped[poi.floor] = {}
      if (!grouped[poi.floor][poi.category]) grouped[poi.floor][poi.category] = []

      grouped[poi.floor][poi.category].push({
        capacity: poi.capacity,
        poiId: poi.poiId,
      })
    })

    return Object.entries(grouped).map(([floor, categories]) => ({
      floor,
      categories: Object.entries(categories).map(([category, pois]) => ({
        category,
        capacities: pois.sort((a, b) => a.capacity - b.capacity),
      })),
    }))
  }

  async getCategoriesByFloor(args) {
    const [floors, categories, pois] = await Promise.all([this.getFloors(), this.getCategories(), this.getPois()])

    const floor = floors.find(f => f.name.toLowerCase() === args.floor.toLowerCase())
    if (!floor) {
      return { error: 'Floor not found' }
    }

    const floorPois = this.filterPoisByFloor(pois, floor.id, categories)
    const categoryNames = this.extractCategoryNames(floorPois, categories)

    return Array.from(categoryNames)
  }

  async getPoisByFloorAndCategory(args) {
    const [floors, categories, pois] = await Promise.all([this.getFloors(), this.getCategories(), this.getPois()])

    const floor = floors.find(f => f.name.toLowerCase() === args.floor.toLowerCase())
    const category = categories.find(c => c.name.toLowerCase() === args.category.toLowerCase())

    if (!floor || !category) {
      return { error: 'Invalid floor or category' }
    }

    return pois.filter(p => p.floorId === floor.id && p.categoryId === category.id).map(p => ({ id: p.id, name: p.name, capacity: p.capacity }))
  }

  async getPoisByCategoryId(args) {
    const categoryId = args.categoryId?.trim()
    if (!categoryId) {
      return { error: 'categoryId is required' }
    }

    const [pois, floors] = await Promise.all([this.getPois(), this.getFloors()])

    return pois
      .filter(p => Array.isArray(p.poiCategories?.ids) && p.poiCategories.ids.includes(categoryId))
      .slice(0, 50) // REASONABLE: Limit results but not too restrictive
      .map(p => ({
        id: p.id,
        name: p.name,
        capacity: p.capacity,
        floor: floors.find(f => f.id === p.floor?.ids?.[0])?.name || 'Unknown',
      }))
  }

  async getAvailableRooms(args) {
    const from = new Date(args.from)
    const until = new Date(args.until)

    const [pois, floors, categories] = await Promise.all([this.getPois(), this.getFloors(), this.getCategories()])

    const availabilityChecks = pois.slice(0, 50).map(poi => this.checkPoiAvailability(poi, from, until)) // KEPT: Original reasonable limit

    const results = await Promise.allSettled(availabilityChecks)

    return results
      .filter(r => r.status === 'fulfilled' && r.value)
      .map(r => r.value)
      .slice(0, CONSTANTS.MAX_AVAILABLE_ROOMS)
      .map(poi => ({
        id: poi.id,
        name: poi.name,
        capacity: poi.capacity,
        floor: floors.find(f => f.id === poi.floorId)?.name || 'Unknown',
        category: categories.find(c => c.id === poi.categoryId)?.name || 'Unknown',
      }))
  }

  async checkPoiAvailability(poi, from, until) {
    try {
      const reservations = await Promise.race([BaseController.getReservationsByPoi(poi.id, from, until), this.timeout(CONSTANTS.TOOL_CALL_TIMEOUT)])

      return Array.isArray(reservations) && reservations.length === 0 ? poi : null
    } catch {
      return null
    }
  }

  async doReservation(args) {
    const [pois, floors, categories, buildings] = await Promise.all([
      this.getPois(),
      this.getFloors(),
      this.getCategories(),
      BaseController.getBuildings(),
    ])

    const poi = pois.find(p => p.id === args.poiId)
    if (!poi) {
      return { error: 'POI not found' }
    }

    const from = new Date(args.from)
    const until = new Date(args.until)

    if (isNaN(from) || isNaN(until)) {
      return { error: 'Invalid time format' }
    }

    // Check for conflicts
    const conflicts = await this.checkBookingConflicts(from, until)
    if (conflicts) return conflicts

    // Get user profile
    const profile = await this.oauthService.getUserProfile()
    const userName = profile?.name || profile?.email || 'Unknown User'
    const userEmail = profile?.email || 'unknown@pia.app'

    // Prepare reservation data
    const reservationData = this.prepareReservationData(poi, from, until, args, {
      userName,
      userEmail,
      floors,
      categories,
      buildings,
    })

    try {
      // Create reservation
      const reservation = await BaseController.createReservation(reservationData)

      // Create appointment
      const appointment = await BaseController.createAppointment(this.prepareAppointmentData(reservation, poi, from, until, userName, userEmail))

      // SIMPLIFIED: Return minimal success data to save tokens
      return {
        status: 'success',
        id: reservation.id,
        room: poi.name,
        start: from.toISOString(),
        end: until.toISOString(),
      }
    } catch (error) {
      return {
        error: error?.message || 'Booking failed',
      }
    }
  }

  async checkBookingConflicts(from, until) {
    const existingBookings = await BaseController.getReservationsByCurrentUserByTime(from, until)

    if (Array.isArray(existingBookings) && existingBookings.length > 0) {
      return {
        error: 'You already have a booking in that time range.',
        conflicts: existingBookings.map(r => ({
          id: r.id,
          room: r.poiName,
          start: r.startTime,
          end: r.endTime,
        })),
      }
    }

    return null
  }

  prepareReservationData(poi, from, until, args, context) {
    const floorId = poi?.floors?.ids?.[0]
    const categoryId = poi.categoryId || poi.poiCategories?.ids?.[0]
    const floor = context.floors.find(f => f.id === floorId)
    const category = context.categories.find(c => c.id === categoryId)
    const building = context.buildings.find(b => b.id === poi.buildingId)

    return {
      title: poi.name || 'Reservation',
      poiId: args.poiId,
      startTime: from.toISOString(),
      endTime: until.toISOString(),
      personCount: args.personCount,
      creatorEmail: context.userEmail,
      creatorName: context.userName,
      poiName: poi.name,
      poiCategory: category?.name || 'Unknown',
      floorNumber: floor?.number || 0,
      floorName: floor?.name || 'Unknown',
      buildingName: building?.name || 'Unknown',
    }
  }

  prepareAppointmentData(reservation, poi, from, until, userName, userEmail) {
    return {
      poiId: poi.id,
      title: poi.name || 'Reservation',
      moderatorEmail: userEmail,
      startTime: from.toISOString(),
      endTime: until.toISOString(),
      reservationId: reservation.id,
      customerId: reservation.customerId,
      creatorEmail: userEmail,
      organizerName: userName,
      location: poi.name,
      campusId: reservation.campusId,
    }
  }

  async getUserReservationsByTime(args) {
    const from = new Date(args.from)
    const until = new Date(args.until)

    const reservations = await BaseController.getReservationsByCurrentUserByTime(from, until)

    return reservations.map(r => ({
      id: r.id,
      room: r.poiName,
      start: r.startTime,
      end: r.endTime,
      floor: r.floorName,
      category: r.poiCategory,
    }))
  }

  getCurrentDatetimeBerlin() {
    this.logger.log('Fetching current datetime in Berlin timezone')
    return moment().tz(CONSTANTS.DEFAULT_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')
  }

  async getDepartures(args) {
    this.logger.log('getDepartures called', args)
    const city = (args.location || 'Hamburg').toLowerCase()
    const allowed = ['hamburg', 'berlin', 'frankfurt', 'munich']

    if (!allowed.includes(city)) {
      this.logger.warn('Unsupported city requested:', city)
      return { error: 'Unsupported city' } // SIMPLIFIED: Error response
    }

    // ADDED: Simple caching for departures
    const cacheKey = `departures_${city}`
    const cached = this.cache.get(cacheKey)
    if (cached) {
      this.logger.log('Using cached departures')
      return cached
    }

    try {
      this.logger.log(`Fetching departures for city: ${city}`)
      const { data } = await axios.get(`https://pinestack-mobility.onrender.com/departures?city=${city}`, {
        timeout: 5000, // ADDED: Timeout
      })

      const allDepartures = data.departures || []

      // KEPT: Original departure count to provide good transport options
      const departures = allDepartures
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .slice(0, CONSTANTS.MAX_DEPARTURES)
        .map(dep => ({
          line: dep.line,
          destination: dep.destination,
          time: dep.time,
          mode: dep.mode,
        }))

      // SIMPLIFIED: Response structure
      const result = { city, departures }
      this.cache.set(cacheKey, result) // Cache for 1 minute (shorter cache for fresh transport data)

      this.logger.log(`Returning ${departures.length} departures`)
      return result
    } catch (err) {
      this.logger.error('Error fetching departures:', err.message)
      return { error: err.message }
    }
  }

  async getDeparturesByCoordinates() {
    const latitude = 53.5529
    const longitude = 10.005

    // ADDED: Simple caching for coordinate departures
    const cacheKey = 'departures_coordinates'
    const cached = this.cache.get(cacheKey)
    if (cached) {
      this.logger.log('Using cached coordinate departures')
      return cached
    }

    const url = `https://pinestack-mobility.onrender.com/departures/by-coordinates?latitude=${latitude}&longitude=${longitude}`

    try {
      const response = await fetch(url, { timeout: 5000 }) // ADDED: Timeout
      const data = await response.json()

      if (!data.departures || !Array.isArray(data.departures)) {
        return { error: 'No departures found' } // SIMPLIFIED: Error response
      }

      // SIMPLIFIED: Group departures more efficiently
      const grouped = {}
      const maxPerStation = 3

      for (const dep of data.departures.slice(0, 15)) {
        // LIMIT: Input processing
        if (!grouped[dep.station]) grouped[dep.station] = []
        if (grouped[dep.station].length < maxPerStation) {
          grouped[dep.station].push({
            line: `${dep.line} (${dep.mode})`,
            destination: `${dep.destination} – ${new Date(dep.time).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}`,
          })
        }
      }

      // SIMPLIFIED: Result format
      const result = Object.entries(grouped)
        .slice(0, 2) // LIMIT: To 2 stations max
        .map(([station, deps]) => ({
          station,
          departures: deps,
        }))

      this.cache.set(cacheKey, result) // Cache for 1 minute (shorter cache for fresh transport data)
      this.logger.log('Coordinate departures processed:', result.length)
      return result
    } catch (error) {
      this.logger.error('Failed to fetch nearest transport stop:', error)
      return { error: 'Failed to fetch nearest stop' }
    }
  }

  timeout(ms) {
    return new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
  }
}

// Create singleton instance
const assistantManager = new OpenAIAssistantManager()

// ENHANCED: Main function with token usage logging
export async function sendToAssistant(message) {
  try {
    const startTime = Date.now()
    const response = await assistantManager.sendMessage(message)
    const duration = Date.now() - startTime

    // Log performance info in development
    if (process.env.NODE_ENV !== 'production') {
      console.log(`🚀 Message processed in ${duration}ms`)
      console.log(`📊 Thread: ${response.messageCount}/${CONSTANTS.MAX_MESSAGES_PER_THREAD} messages`)

      if (response.cacheStats && response.cacheStats.size > 0) {
        console.log(`💾 Cache: ${response.cacheStats.size} items`)
      }
    }

    return response
  } catch (error) {
    console.error('❌ sendToAssistant failed:', error)
    return {
      reply: '❌ Sorry, I encountered an unexpected error. Please try again.',
      tokenUsage: { inputTokens: 0, outputTokens: 0, totalTokens: 0 },
      error: true,
    }
  }
}

// NEW: Utility functions for monitoring
export function getAssistantStats() {
  return assistantManager.getStats()
}

export function clearCache() {
  assistantManager.cache.clear()
  console.log('🗑️ Cache cleared')
}

export async function forceThreadRotation() {
  await assistantManager.rotateThread()
  console.log('🔄 Thread rotation forced')
}

// Export classes and constants
export { OpenAIAssistantManager, ToolHandler, CacheManager, Logger, CONSTANTS }
